import { Award, Plus, Star, TriangleAlert, Trophy } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { toast } from '@core/toast';
import { Spinner } from '../../../components/spinner';
import { AssignGoalModal } from './assign-goal-modal';
import { RemoveBadgeModal } from './remove-badge-modal';
import { useUserBadgesStore, UserBadgeV2AdminDto } from './store';
import { UserBadgesTable } from './user-badges-table';

const EmptyBadgesState = ({ onAssign }: { onAssign: () => void }) => (
    <Card className="border-dashed bg-primary/5">
        <CardContent className="flex flex-col items-center px-8 py-12 text-center">
            <div className="mb-6 flex items-center justify-center gap-2">
                <Award className="size-9 -rotate-12 text-[#CD7F32] opacity-70" />
                <Trophy className="size-12 text-[#FFD700] opacity-90" />
                <Star className="size-9 rotate-12 text-[#C0C0C0] opacity-70" />
            </div>
            <h3 className="mb-1 text-lg font-semibold">No Badges Earned Yet</h3>
            <p className="mb-6 max-w-[360px] text-sm text-muted-foreground">
                This user hasn't earned any badges yet. Assign a badge goal to
                help them start earning achievements.
            </p>
            <Button onClick={onAssign}>
                <Plus />
                Assign Badge Goal
            </Button>
        </CardContent>
    </Card>
);

export const UserBadges = observer(() => {
    const {
        userBadges,
        sorting,
        setSorting,
        dispatch,
        removeBadge,
        deleteBadgePermanently,
        refreshBadges,
        userId
    } = useUserBadgesStore();

    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedBadge, setSelectedBadge] =
        useState<UserBadgeV2AdminDto | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleAssignSuccess = () => {
        refreshBadges();
    };

    const handleRemoveBadge = (badge: UserBadgeV2AdminDto) => {
        setSelectedBadge(badge);
        setRemoveModalOpen(true);
    };

    const handleDeleteBadge = (badge: UserBadgeV2AdminDto) => {
        setSelectedBadge(badge);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setSelectedBadge(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedBadge) return;
        setIsDeleting(true);
        try {
            await deleteBadgePermanently(selectedBadge.id);
            toast.success('Badge permanently deleted');
            closeDeleteModal();
            refreshBadges();
        } catch (error) {
            toast.error('Failed to delete badge');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRemoveConfirm = async (badgeId: number, reason?: string) => {
        await removeBadge(badgeId, reason);
        refreshBadges();
    };

    const handleSortingChange = (newSorting?: {
        id: string;
        desc?: boolean;
    }) => {
        if (newSorting) {
            setSorting({ id: newSorting.id, desc: newSorting.desc ?? false });
        }
    };

    const hasBadges = userBadges.$?.content && userBadges.$.content.length > 0;
    const badgeCount = userBadges.$?.totalElements || 0;

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Trophy className="size-5 text-warning" />
                    <h2 className="text-lg font-semibold tracking-tight">
                        User Badges
                    </h2>
                    {badgeCount > 0 && <Badge>{badgeCount}</Badge>}
                </div>
                {hasBadges && (
                    <Button onClick={() => setAssignModalOpen(true)}>
                        <Plus />
                        Assign Badge Goal
                    </Button>
                )}
            </div>

            {userBadges.match({
                loading: () => (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                ),
                error: (error) => (
                    <Alert variant="destructive">
                        <AlertDescription>
                            Error loading badges: {error.message}
                        </AlertDescription>
                    </Alert>
                ),
                empty: () => (
                    <EmptyBadgesState
                        onAssign={() => setAssignModalOpen(true)}
                    />
                ),
                fetched: () =>
                    !hasBadges ? (
                        <EmptyBadgesState
                            onAssign={() => setAssignModalOpen(true)}
                        />
                    ) : (
                        <Card>
                            <CardContent className="p-0">
                                <UserBadgesTable
                                    userBadges={userBadges}
                                    sorting={sorting}
                                    onSortingChange={handleSortingChange}
                                    onPageChange={(page) =>
                                        dispatch({ type: 'page', page })
                                    }
                                    onPageSizeChange={(size) =>
                                        dispatch({ type: 'size', size })
                                    }
                                    onRemoveBadge={handleRemoveBadge}
                                    onDeleteBadge={handleDeleteBadge}
                                />
                            </CardContent>
                        </Card>
                    )
            })}

            <AssignGoalModal
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                userId={userId}
                onSuccess={handleAssignSuccess}
            />

            <RemoveBadgeModal
                open={removeModalOpen}
                onClose={() => {
                    setRemoveModalOpen(false);
                    setSelectedBadge(null);
                }}
                badge={selectedBadge}
                onConfirm={handleRemoveConfirm}
            />

            <Dialog
                open={deleteModalOpen}
                onOpenChange={(open) => !open && closeDeleteModal()}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TriangleAlert className="size-5 text-destructive" />
                            Permanently Delete Badge
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to permanently delete the
                            badge{' '}
                            <strong>
                                {selectedBadge?.badgeLevelName?.en ||
                                    'this badge'}
                            </strong>
                            ? This will completely remove all record of this
                            badge from the system.
                        </DialogDescription>
                    </DialogHeader>
                    <Alert variant="destructive">
                        <AlertDescription>
                            This action cannot be undone. The badge will be
                            permanently removed from the database.
                        </AlertDescription>
                    </Alert>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={closeDeleteModal}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteConfirm}
                            loading={isDeleting}
                        >
                            Delete Permanently
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
});

export default UserBadges;
